import { useState } from "react";
import {
  Sparkles, TrendingUp, Users, Eye, ShoppingBag, Wallet, Loader2, Send, Bell, Radio, Lightbulb,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatPrice, stripMd } from "@/lib/format";
import { useVendorInsights, useAiNotifications } from "@/hooks/useAi";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-line bg-white p-4">
      <div className="mb-2 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 text-white">
        <Icon className="h-4.5 w-4.5" />
      </div>
      <p className="font-display text-xl font-black text-ink">{value}</p>
      <p className="text-xs font-semibold text-slate-500">{label}</p>
    </div>
  );
}

const QUICK = [
  "Combien ai-je vendu aujourd'hui ?",
  "Quel est mon produit le plus vendu ?",
  "Quels produits sont beaucoup consultés ?",
  "Donne-moi une recommandation pour vendre plus",
];

export default function VendorAi() {
  const { data: insights, isLoading } = useVendorInsights();
  const { data: notifs, refetch } = useAiNotifications();
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState("");
  const [chatting, setChatting] = useState(false);

  const send = async (text) => {
    const t = (text ?? msg).trim();
    if (!t || chatting) return;
    setMsg("");
    setChat((p) => [...p, { role: "user", content: t }]);
    setChatting(true);
    try {
      const { data } = await api.post("/ai/vendor/chat", { message: t, session_id: "vendor-chat" });
      setChat((p) => [...p, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setChat((p) => [...p, { role: "assistant", content: apiError(e) }]);
    } finally {
      setChatting(false);
    }
  };

  const markRead = async (id) => {
    await api.post(`/ai/notifications/${id}/read`).catch(() => {});
    refetch();
  };

  if (isLoading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>;

  const s = insights?.stats || {};

  return (
    <div className="space-y-6" data-testid="vendor-ai">
      <div className="flex items-center gap-3 rounded-3xl bg-gradient-to-br from-violet-600 to-indigo-700 p-6 text-white">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15"><Sparkles className="h-6 w-6" /></span>
        <div>
          <h1 className="font-display text-xl font-black sm:text-2xl">ISMO-TECH AI — Assistant vendeur</h1>
          <p className="text-sm text-white/80">Analyses, alertes et recommandations pour votre boutique</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat icon={Wallet} label="Chiffre d'affaires" value={formatPrice(s.revenue || 0)} />
        <Stat icon={TrendingUp} label="Ventes aujourd'hui" value={formatPrice(s.revenue_today || 0)} />
        <Stat icon={ShoppingBag} label="Commandes" value={s.orders_count || 0} />
        <Stat icon={Radio} label="Visiteurs actifs" value={s.active_visitors || 0} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Alertes IA */}
        <div className="rounded-2xl border border-line bg-white p-4">
          <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-extrabold text-ink"><Lightbulb className="h-4 w-4 text-amber-500" /> Alertes IA</h3>
          <div className="space-y-2">
            {(insights?.alerts || []).map((al, i) => (
              <div key={i} className="rounded-xl border border-amber-100 bg-amber-50/60 px-3 py-2 text-[12.5px] text-ink">{al.text}</div>
            ))}
          </div>
        </div>

        {/* Produits les plus consultés */}
        <div className="rounded-2xl border border-line bg-white p-4">
          <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-extrabold text-ink"><Eye className="h-4 w-4 text-violet-600" /> Produits les plus consultés</h3>
          {(insights?.top_viewed || []).length ? (
            <ol className="space-y-2">
              {insights.top_viewed.map((t, i) => (
                <li key={i} className="flex items-center justify-between text-[13px]">
                  <span className="flex items-center gap-2"><span className="flex h-5 w-5 items-center justify-center rounded-full bg-violet-100 text-[10px] font-black text-violet-700">{i + 1}</span>{t.name}</span>
                  <span className="font-bold text-slate-500">{t.views} vues</span>
                </li>
              ))}
            </ol>
          ) : <p className="text-xs text-slate-400">Pas encore de consultations.</p>}
        </div>

        {/* Clients fidèles */}
        <div className="rounded-2xl border border-line bg-white p-4">
          <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-extrabold text-ink"><Users className="h-4 w-4 text-violet-600" /> Clients les plus actifs</h3>
          {(insights?.loyal_clients || []).length ? (
            <ol className="space-y-2">
              {insights.loyal_clients.map((c, i) => (
                <li key={i} className="flex items-center justify-between text-[13px]">
                  <span className="truncate">{c.name || "Client"}</span>
                  <span className="text-[11px] text-slate-500">{c.orders} cmd · {formatPrice(c.spent)}</span>
                </li>
              ))}
            </ol>
          ) : <p className="text-xs text-slate-400">Aucun client pour le moment.</p>}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Notifications IA */}
        <div className="rounded-2xl border border-line bg-white p-4" data-testid="vendor-ai-notifications">
          <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-extrabold text-ink"><Bell className="h-4 w-4 text-violet-600" /> Notifications IA</h3>
          <div className="max-h-72 space-y-2 overflow-y-auto">
            {(notifs?.items || []).length ? notifs.items.map((n) => (
              <button key={n.id} onClick={() => markRead(n.id)} className={`block w-full rounded-xl border p-3 text-left transition-colors hover:border-violet-300 ${n.read ? "border-line" : "border-violet-200 bg-violet-50/60"}`}>
                <div className="flex items-center justify-between">
                  <p className="text-[13px] font-bold text-ink">{n.title}</p>
                  {!n.read && <span className="h-2 w-2 rounded-full bg-violet-500" />}
                </div>
                <p className="mt-0.5 whitespace-pre-wrap text-[11.5px] text-slate-500">{n.message}</p>
              </button>
            )) : <p className="text-xs text-slate-400">Aucune notification.</p>}
          </div>
        </div>

        {/* Chat assistant */}
        <div className="flex flex-col rounded-2xl border border-line bg-white p-4">
          <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-extrabold text-ink"><Sparkles className="h-4 w-4 text-violet-600" /> Demander à l'assistant</h3>
          <div className="mb-3 max-h-56 flex-1 space-y-2 overflow-y-auto">
            {chat.length === 0 && (
              <div className="flex flex-wrap gap-1.5">
                {QUICK.map((q) => (
                  <button key={q} data-testid="vendor-ai-quick" onClick={() => send(q)} className="rounded-full border border-violet-200 px-2.5 py-1 text-[11px] font-semibold text-violet-700 hover:bg-violet-50">{q}</button>
                ))}
              </div>
            )}
            {chat.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-[12.5px] ${m.role === "user" ? "bg-violet-600 text-white" : "border border-line bg-surface text-ink"}`}>{stripMd(m.content)}</div>
              </div>
            ))}
            {chatting && <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Analyse…</div>}
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="vendor-ai-input" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Posez une question sur votre boutique…" className="flex-1 rounded-full border border-line bg-surface px-3.5 py-2 text-[13px] outline-none focus:border-violet-500" />
            <button data-testid="vendor-ai-send" onClick={() => send()} disabled={chatting} className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-600 text-white disabled:opacity-50">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
